import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  TextField,
  Autocomplete,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { apiService } from "../../config/api";
import { authenticatedApi } from "../../utils/api";

interface Metric {
  key: string;
  label: string;
  type: "INTEGER" | "DECIMAL";
  unit: string;
  min: number | null;
  max: number | null;
  step: number;
}

interface Threshold {
  metric: string;
  value: number | string;
}

interface AdherenceThresholdEditorProps {
  open: boolean;
  studyId: string;
  onClose: () => void;
}

const AdherenceThresholdEditor: React.FC<AdherenceThresholdEditorProps> = ({
  open,
  studyId,
  onClose,
}) => {
  const [thresholds, setThresholds] = useState<Threshold[]>([]);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await apiService.getAuthToken();
      if (!token) throw new Error("Authentication required");

      // Fetch available metrics
      const metricsResponse = await authenticatedApi.get<any>(
        "/api/adherence-thresholds/metrics",
        token
      );
      if (metricsResponse.success) {
        setMetrics(metricsResponse.metrics);
      }

      // Fetch current thresholds for study
      const thresholdsResponse = await authenticatedApi.get<any>(
        `/api/adherence-thresholds/${studyId}`,
        token
      );
      if (thresholdsResponse.success) {
        setThresholds(
          thresholdsResponse.thresholds.filter(
            (t: { metric: string }) => t.metric !== "min_hr_bpm"
          )
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [studyId]);

  // Fetch metrics and current thresholds on open
  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, loadData]);

  const handleAddRow = () => {
    setThresholds([...thresholds, { metric: "", value: "" }]);
  };

  const handleRemoveRow = (index: number) => {
    setThresholds(thresholds.filter((_, i) => i !== index));
  };

  const handleThresholdChange = (
    index: number,
    field: "metric" | "value",
    value: any
  ) => {
    const newThresholds = [...thresholds];
    if (field === "value") {
      newThresholds[index].value = value ? Number(value) : "";
    } else {
      newThresholds[index].metric = value;
    }
    setThresholds(newThresholds);
  };

  const validateThresholds = (): string | null => {
    if (thresholds.length === 0) {
      return "At least one metric is required";
    }

    const seenMetrics = new Set<string>();

    for (const t of thresholds) {
      if (!t.metric) {
        return "All rows must have a metric selected";
      }
      if (seenMetrics.has(t.metric)) {
        return `Metric "${t.metric}" is duplicated`;
      }
      seenMetrics.add(t.metric);

      if (t.value === "" || t.value === null || t.value === 0) {
        return `Metric "${t.metric}" must have a non-zero value`;
      }

      // Validate against metric constraints
      const metric = metrics.find((m) => m.key === t.metric);
      if (metric) {
        const numValue = Number(t.value);
        if (metric.min !== null && numValue < metric.min) {
          return `${metric.label} must be at least ${metric.min}`;
        }
        if (metric.max !== null && numValue > metric.max) {
          return `${metric.label} cannot exceed ${metric.max}`;
        }
      }
    }

    return null;
  };

  const handleSave = async () => {
    const validationError = validateThresholds();
    if (validationError) {
      setSnackbar({
        open: true,
        message: validationError,
        severity: "error",
      });
      return;
    }

    setSaving(true);
    try {
      const token = await apiService.getAuthToken();
      if (!token) throw new Error("Authentication required");

      const response = await authenticatedApi.put<any>(
        `/api/adherence-thresholds/${studyId}`,
        { thresholds },
        token
      );

      if (response.success) {
        setSnackbar({
          open: true,
          message: "Thresholds saved successfully",
          severity: "success",
        });
        setTimeout(() => onClose(), 1500);
      } else {
        setSnackbar({
          open: true,
          message: response.error || "Failed to save thresholds",
          severity: "error",
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : "Failed to save thresholds",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleResetConfirmed = async () => {
    setShowResetConfirm(false);
    setSaving(true);
    try {
      const token = await apiService.getAuthToken();
      if (!token) throw new Error("Authentication required");

      const response = await authenticatedApi.delete<any>(
        `/api/adherence-thresholds/${studyId}`,
        token
      );

      if (response.success) {
        onClose();
      } else {
        setSnackbar({
          open: true,
          message: response.error || "Failed to reset thresholds",
          severity: "error",
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : "Failed to reset thresholds",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // Get available metrics (exclude already selected ones)
  const getAvailableMetrics = (currentIndex: number) => {
    const selectedMetrics = thresholds
      .map((t, i) => (i !== currentIndex ? t.metric : null))
      .filter(Boolean);
    return metrics.filter((m) => !selectedMetrics.includes(m.key));
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Adherence Thresholds</DialogTitle>
        <DialogContent sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Adherence Thresholds
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Define the minimum daily values for each metric. Adherence is
              calculated as the average percentage across all metrics.
            </Typography>

            {thresholds.length === 0 && (
              <Alert severity="info">No thresholds configured. Click the + button to add metrics.</Alert>
            )}

            {/* Threshold Rows */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {thresholds.map((threshold, index) => {
                const selectedMetric = metrics.find(
                  (m) => m.key === threshold.metric
                );
                const availableMetrics = getAvailableMetrics(index);

                return (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      alignItems: "flex-start",
                      p: 1.5,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      bgcolor: "background.paper",
                    }}
                  >
                    {/* Metric Dropdown */}
                    <Autocomplete
                      options={availableMetrics}
                      getOptionLabel={(option) => option.label}
                      value={selectedMetric || null}
                      onChange={(_, value) =>
                        handleThresholdChange(
                          index,
                          "metric",
                          value ? value.key : ""
                        )
                      }
                      filterOptions={(options, { inputValue }) =>
                        options.filter((option) =>
                          option.label
                            .toLowerCase()
                            .includes(inputValue.toLowerCase())
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Metric"
                          size="small"
                          error={!threshold.metric && index < thresholds.length}
                        />
                      )}
                      sx={{ flex: 1, minWidth: 200 }}
                    />

                    {/* Value Input */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TextField
                        type="number"
                        label="Value"
                        size="small"
                        value={threshold.value}
                        onChange={(e) =>
                          handleThresholdChange(index, "value", e.target.value)
                        }
                        inputProps={{
                          min: selectedMetric?.min || 0,
                          max: selectedMetric?.max,
                          step: selectedMetric?.step || 1,
                        }}
                        sx={{ width: 100 }}
                        error={threshold.value === "" && threshold.metric !== ""}
                      />
                      {selectedMetric && (
                        <Typography variant="caption" color="textSecondary" sx={{ minWidth: 60 }}>
                          {selectedMetric.unit}
                        </Typography>
                      )}
                    </Box>

                    {/* Delete Button */}
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveRow(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>

            {/* Add Row Button */}
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddRow}
              variant="outlined"
              sx={{ alignSelf: "flex-start", mt: 1 }}
              disabled={thresholds.length >= metrics.length}
            >
              Add Metric
            </Button>
          </Box>
        </DialogContent>

        <DialogActions sx={{ gap: 1, p: 2 }}>
          <Button onClick={() => setShowResetConfirm(true)} variant="outlined" disabled={saving}>
            Reset to Default
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || thresholds.length === 0}
          >
            {saving ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reset to Defaults?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove all custom thresholds and revert to the default
            values. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowResetConfirm(false)}>Cancel</Button>
          <Button
            onClick={handleResetConfirmed}
            color="error"
            variant="contained"
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdherenceThresholdEditor;
